'use client';

import { Button, Drawer, Grid, Heading, Icon, Row, SelectField, TextField, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { type MouseEvent, useCallback, useEffect, useId, useState } from 'react';

import { useCustomAddressStore, zipCodeSetter } from '@libs/common-next';

import { ErrorMessage } from './error-message';
import { localized } from './sdd-localize';
import { StreetAddressInput } from './street-address-input';
import { useCustomAddressDrawerStore } from './use-custom-address-drawer-store';
import { useSddAddressForm } from './use-sdd-address-form';
import './SddAddressDrawer.scss';

export type SddAddressDrawerProps = Readonly<{
    showBackButton?: boolean;
    /** When the host drawer is closed, the stacked SDD address drawer should close too. */
    forceClosed?: boolean;
    /** Reports the address validation error state to an optional host. */
    onErrorChange?: (hasError: boolean) => void;
    onOpenChange?: (isOpen: boolean) => void;
    /** Sparky close (X / overlay / Esc). Used when stacked over another drawer. */
    onCloseAll?: () => void;
    /** Mini PDP shows Add address / Change in delivery details. PDP keeps its own trigger there. */
    showTrigger?: boolean;
    /** Controlled open state for stacked Mini PDP. Uncontrolled stacked mode uses internal state. */
    isOpen?: boolean;
    isDeliveryAvailable: boolean;
    isLoading: boolean;
    isError: boolean;
}>;

export const SddAddressDrawer = ({
    showBackButton = false,
    forceClosed = false,
    onErrorChange,
    onOpenChange,
    onCloseAll,
    showTrigger = showBackButton,
    isOpen,
    isDeliveryAvailable,
    isLoading,
    isError,
}: SddAddressDrawerProps) => {
    const storeIsOpen = useCustomAddressDrawerStore(state => state.isOpen);
    const closeStore = useCustomAddressDrawerStore(state => state.close);
    const submittedStreet = useCustomAddressStore(state => state.address?.streetLine1);
    const [internalIsActive, setInternalIsActive] = useState(false);
    const headingId = useId();
    const [hasBeenRendered, setHasBeenRendered] = useState(false);
    const isStackedControlled = isOpen !== undefined;
    const isStackedOpen = isStackedControlled ? isOpen : internalIsActive;
    const isActive = showBackButton ? isStackedOpen : storeIsOpen;

    const closeTopDrawer = useCallback(() => {
        if (showBackButton) {
            if (!isStackedControlled) {
                setInternalIsActive(false);
            }
        } else {
            closeStore();
        }
        onOpenChange?.(false);
    }, [closeStore, isStackedControlled, onOpenChange, showBackButton]);

    const {
        register,
        setValue,
        trigger,
        formState: { errors },
        watch,
        onSubmit,
        handleAddressSelect,
        handleFormReset,
        isValidating,
        errorType,
        formKey,
        showComplement,
        toggleComplement,
    } = useSddAddressForm({
        onSuccessAction: closeTopDrawer,
        isDeliveryAvailable,
        isLoading,
        isError,
    });

    useEffect(() => {
        setHasBeenRendered(true);
    }, []);

    useEffect(() => {
        if (forceClosed) {
            setInternalIsActive(false);
            onOpenChange?.(false);
        }
    }, [forceClosed, onOpenChange]);

    useEffect(() => {
        onErrorChange?.(isActive && errorType === 'apiError');
    }, [errorType, isActive, onErrorChange]);

    useEffect(() => {
        handleFormReset();
    }, [handleFormReset, isActive]);

    const handleBack = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        closeTopDrawer();
    };

    const handleClose = () => {
        closeTopDrawer();
        onCloseAll?.();
    };

    return (
        <>
            {showTrigger && (
                <Button
                    type="button"
                    variant="link"
                    className="sdd-address-drawer__trigger"
                    text={submittedStreet ? 'Change' : 'Add address'}
                    data-testid="sdd-address-drawer-trigger"
                    onClick={() => {
                        if (!isStackedControlled) {
                            setInternalIsActive(true);
                        }
                        onOpenChange?.(true);
                    }}
                />
            )}
            {hasBeenRendered && (
                <Drawer
                    align="right"
                    isActive={isActive}
                    onClose={handleClose}
                    data-testid="sdd-drawer"
                    className={cn('sdd-address-drawer', { 'sdd-address-drawer--stacked': showBackButton })}
                    ariaLabelledBy={headingId}
                >
                    <Drawer.Header className="sdd-address-drawer__header">
                        {showBackButton && (
                            <button
                                type="button"
                                aria-label="Back"
                                className="sdd-address-drawer__back"
                                data-testid="sdd-address-drawer-back-btn"
                                onClick={handleBack}
                            >
                                <Icon name="chevron-left" size="lg" aria-hidden="true" />
                            </button>
                        )}
                        <Heading id={headingId} tagName="h2" size="headline" data-testid="drawer-title">
                            Same-day delivery
                        </Heading>
                    </Drawer.Header>
                    <Drawer.Body key={formKey}>
                        {errorType && <ErrorMessage errorType={errorType} />}
                        <TextPassage className="check-availability">
                            Please provide more details to confirm same-day delivery is available in your area
                        </TextPassage>
                        <Row className="form-row sdd-form-input__street">
                            <StreetAddressInput
                                value={watch('streetLine1')}
                                onAddressSelect={handleAddressSelect}
                                isError={Boolean(errors.streetLine1)}
                                fieldNote={errors.streetLine1?.message}
                                {...register('streetLine1')}
                            />
                        </Row>

                        <Row className="form-row">
                            {showComplement ? (
                                <TextField
                                    data-cs-mask
                                    data-testid="sdd-complement"
                                    className="sdd-form-input"
                                    label="Apt/suite/other"
                                    type="text"
                                    {...register('streetLine2')}
                                />
                            ) : (
                                <Button
                                    iconName="plus"
                                    data-testid="sdd-show-complement"
                                    text="Add apt/suite/other"
                                    variant="link"
                                    onClick={toggleComplement}
                                />
                            )}
                        </Row>

                        <Row className="form-row">
                            <Grid variant="side-by-side" gap="sm" className="sdd-form-row-split">
                                <Grid.Item>
                                    <TextField
                                        data-cs-mask
                                        data-testid="sdd-zip"
                                        className="sdd-form-input"
                                        label={localized.zipPostalCode}
                                        type="text"
                                        isError={Boolean(errors.zipPostalCode)}
                                        fieldNote={errors.zipPostalCode?.message}
                                        {...register('zipPostalCode', {
                                            onChange: ev => {
                                                zipCodeSetter(ev, 'zip', (_, value) => {
                                                    (ev.target as HTMLInputElement).value = value;
                                                });
                                            },
                                            onBlur: () => {
                                                trigger('zipPostalCode');
                                            },
                                        })}
                                    />
                                </Grid.Item>
                                <Grid.Item>
                                    <SelectField
                                        data-cs-mask
                                        data-testid="sdd-state"
                                        className="sdd-form-input"
                                        label={localized.stateProvinceLabel}
                                        isError={Boolean(errors.stateProvince)}
                                        fieldNote={errors.stateProvince?.message}
                                        value={watch('stateProvince')}
                                        onChange={ev => {
                                            const value = (ev.target as HTMLSelectElement).value;
                                            setValue('stateProvince', value, {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                            });
                                            trigger('stateProvince');
                                        }}
                                        items={localized.stateProvinceSelection.map(
                                            ({ abbr, name }: { abbr: string; name: string }) => ({
                                                label: name,
                                                value: abbr,
                                                disabled: false,
                                            })
                                        )}
                                    />
                                </Grid.Item>
                            </Grid>
                        </Row>

                        <Row className="form-row">
                            <TextField
                                data-cs-mask
                                data-testid="sdd-city"
                                className="sdd-form-input"
                                label="City"
                                type="text"
                                isError={Boolean(errors.city)}
                                fieldNote={errors.city?.message}
                                {...register('city')}
                            />
                        </Row>

                        <Row className="form-row">
                            <Button
                                fullWidth
                                text="Check address"
                                variant="primary"
                                type="button"
                                data-testid="sdd-check-address"
                                loading={isValidating}
                                onClick={onSubmit}
                            />
                        </Row>
                    </Drawer.Body>
                </Drawer>
            )}
        </>
    );
};
